import type PageDataContext from "@core/Context/PageDataContext";
import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticleRefService from "@core-ui/ContextServices/ArticleRef";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import {
	ExistingResourceStoreProvider,
	ResourceServiceContext,
} from "@core-ui/ContextServices/ResourceService/store/ResourceStore.provider";
import debounceFunction from "@core-ui/debounceFunction";
import { CatalogStoreProvider } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import ProseMirrorDiffLineComponent from "@ext/git/core/Diff/components/ProseMirrorDiffLine";
import caretInText from "@ext/git/core/Diff/logic/caretInText";
import collapseDeleted from "@ext/git/core/Diff/logic/collapseDeleted";
import deletedRestore from "@ext/git/core/Diff/logic/deletedRestore";
import inlineComponentChanges, { inlineComponentChangesKey } from "@ext/git/core/Diff/logic/inlineComponentChanges";
import insertedText from "@ext/git/core/Diff/logic/insertedText";
import linkChanges, { linkChangesKey } from "@ext/git/core/Diff/logic/linkChanges";
import type { ProseMirrorDiffLine } from "@ext/git/core/Diff/logic/model/ProseMirrorDiffLine";
import type { MovedMarker } from "@ext/git/core/Diff/logic/movedMarkers";
import movedMarkersPlugin, { movedMarkersKey } from "@ext/git/core/Diff/logic/movedMarkersPlugin";
import onlyChanges, { visiblePos } from "@ext/git/core/Diff/logic/onlyChanges";
import { restoreInserted, restoreModified } from "@ext/git/core/Diff/logic/restoreChange";
import type { TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import { getCachedSetting } from "@ext/settings/logic/cachedSettingsStore";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import { type Editor, Extension } from "@tiptap/core";
import type { PluginView } from "@tiptap/pm/state";
import { TooltipProvider } from "@ui-kit/Tooltip";
import { Plugin, PluginKey } from "prosemirror-state";
import type { MutableRefObject, ReactElement } from "react";
import { createRoot, type Root } from "react-dom/client";

export const DIFF_DEBOUNCE_DELAY = 300;

export interface DiffExtensionProps {
	articleRef: MutableRefObject<HTMLDivElement>;
	apiUrlCreator: ApiUrlCreator;
	articlePath: string;
	isLeftPinned: boolean;
	oldScope: TreeReadScope;
	newScope: TreeReadScope;
	pageDataContext: PageDataContext;
	catalogProps: ClientCatalogProps;
}

export interface DiffExtensionStore {
	diffLines: ProseMirrorDiffLine[];
	movedMarkers: MovedMarker[];
	changes: DiffChange[];
	isLeftPinned: boolean;
	// Read at use time, not captured at creation: the live editor enables compare after mount.
	articlePath: string;
	oldScope: TreeReadScope;
	newScope: TreeReadScope;
}

export interface DiffContext {
	articlePath: string;
	oldScope: TreeReadScope;
	newScope: TreeReadScope;
}

declare module "@tiptap/core" {
	interface Storage {
		diffLines: DiffExtensionStore;
	}

	interface Commands<ReturnType> {
		diffLines: {
			updateDiffLinesModel: (diffLines: ProseMirrorDiffLine[]) => ReturnType;
			updateDiffChanges: (changes: DiffChange[]) => ReturnType;
			updateMovedMarkers: (markers: MovedMarker[]) => ReturnType;
			updateDiffContext: (context: DiffContext) => ReturnType;
			updateIsLeftPinned: (isLeftPinned: boolean, triggerUpdate?: boolean) => ReturnType;
		};
	}
}

type LayerOrigin = { left: number; top: number; textLeft: number };
type LineGeometry = { top: number; left: number; height: number };

const createLayer = () => {
	const layer = document.createElement("div");
	layer.className = "diff-lines-layer";
	Object.assign(layer.style, { position: "relative", zIndex: "1", height: "0" });
	return layer;
};

class DiffLines implements PluginView {
	// Per instance: a shared key lets one editor's late call silently cancel another's.
	private readonly _debounceSymbol = Symbol();
	private readonly _diffLinesStratchPixels = -1;
	private readonly _tableLineInset = 6;
	private readonly _deletedDiffLineHeight = 5;

	private _layer: HTMLDivElement | null = null;

	private static _editorRenderData: Map<Editor, { root: Root; element: HTMLElement }[]> = new Map();
	private _onEditorDestroyBounded: () => void;
	private _extensionStore: DiffExtensionStore;

	private _resizeObserver: ResizeObserver | null = null;
	private readonly _observedWrappers = new Set<Element>();

	constructor(private _editor: Editor) {
		this._onEditorDestroyBounded = this._onEditorDestroy.bind(this);
		this._editor.on("destroy", this._onEditorDestroyBounded);
		this._extensionStore = this._editor.storage.diffLines;

		if (typeof ResizeObserver !== "undefined") {
			this._resizeObserver = new ResizeObserver(() => this.update());
			this._resizeObserver.observe(this._editor.view.dom);
		}
	}

	private _getLayer(): HTMLDivElement | null {
		const dom = this._editor.view.dom;
		const parent = dom.parentElement;
		if (!parent) return null;
		if (this._layer?.parentElement !== parent) {
			this._layer ??= createLayer();
			parent.insertBefore(this._layer, dom);
		}
		return this._layer;
	}

	private _articleRef(): HTMLDivElement {
		return getEditorContext(this._editor).articleRef?.current;
	}

	update() {
		debounceFunction(this._debounceSymbol, this._update.bind(this), DIFF_DEBOUNCE_DELAY);
	}

	destroy() {
		this._resizeObserver?.disconnect();
		this._resizeObserver = null;
		this._observedWrappers.clear();
		this._editor.off("destroy", this._onEditorDestroyBounded);
	}

	private _update() {
		if (this._editor.isDestroyed) return;
		const layer = this._getLayer();
		if (!layer) return;

		const diffLines = this._extensionStore.diffLines;

		let renderData = this._getRenderData();

		if (diffLines?.length > renderData?.length) {
			this._addNewRenderData(diffLines.length - renderData.length);
		} else if (diffLines?.length < renderData?.length) {
			this._removeOldRenderData(renderData.length - diffLines.length);
		}

		renderData = this._getRenderData();
		this._observeWidthWrappers();
		const geometry = this._measure(layer, diffLines);

		const { apiUrlCreator, pageDataContext, catalogProps, resourceService, resourceStore } = getEditorContext(
			this._editor,
		);
		// Separate React root: without the article's resource service and store, images in the
		// old version crash into the error boundary or stay skeletons.
		const withStore = (children: ReactElement) =>
			resourceStore ? (
				<ExistingResourceStoreProvider store={resourceStore}>{children}</ExistingResourceStoreProvider>
			) : (
				children
			);
		const withResources = (children: ReactElement) =>
			withStore(
				resourceService ? (
					<ResourceServiceContext.Provider value={resourceService}>
						{children}
					</ResourceServiceContext.Provider>
				) : (
					children
				),
			);
		const articleRef = this._articleRef();

		renderData.forEach((rootData, idx) => {
			const diffLine = diffLines[idx];
			const { top, left, height } = geometry[idx];
			const uniqueKey = `${diffLine.pos.from}-${diffLine.pos.to}`;
			// Navigation finds the target line by this attribute.
			rootData.element.dataset.from = String(diffLine.pos.from);

			rootData.root.render(
				withResources(
					<TooltipProvider>
						<PageDataContextService.Provider value={pageDataContext}>
							<CatalogStoreProvider data={catalogProps}>
								<ApiUrlCreatorService.Provider value={apiUrlCreator}>
									<ArticleRefService.Provider value={articleRef}>
										<ProseMirrorDiffLineComponent
											articlePath={this._extensionStore.articlePath}
											diffLine={diffLine}
											height={height}
											key={uniqueKey}
											left={left}
											oldScope={this._extensionStore.oldScope}
											onDiscard={this._getOnDiscard(diffLine)}
											top={top}
										/>
									</ArticleRefService.Provider>
								</ApiUrlCreatorService.Provider>
							</CatalogStoreProvider>
						</PageDataContextService.Provider>
					</TooltipProvider>,
				),
			);
		});
	}

	// A wide table spreads past the text column once its widths apply, without resizing the editor.
	private _observeWidthWrappers() {
		const observer = this._resizeObserver;
		if (!observer) return;
		const wrappers = new Set(this._editor.view.dom.querySelectorAll(".width-wrapper"));
		this._observedWrappers.forEach((wrapper) => {
			if (wrappers.has(wrapper)) return;
			observer.unobserve(wrapper);
			this._observedWrappers.delete(wrapper);
		});
		wrappers.forEach((wrapper) => {
			if (this._observedWrappers.has(wrapper)) return;
			observer.observe(wrapper);
			this._observedWrappers.add(wrapper);
		});
	}

	private _measure(layer: HTMLElement, diffLines: ProseMirrorDiffLine[]): LineGeometry[] {
		const origin = this._origin(layer);
		const axis = this._columnAxis();
		return diffLines.map((diffLine) => this._geometry(diffLine, origin, axis));
	}

	private _getOnDiscard(diffLine: ProseMirrorDiffLine) {
		if (!this._editor.isEditable) return;
		if (diffLine.type === "added") {
			const { pos } = diffLine;
			if (!restoreInserted(this._editor.state, pos)) return;
			return () => {
				if (!this._editor.isEditable) return;
				const tr = restoreInserted(this._editor.state, pos);
				if (tr) this._editor.view.dispatch(tr);
			};
		}
		if (diffLine.type !== "modified" || !diffLine.oldNode) return;
		const { oldNode, pos } = diffLine;
		if (!restoreModified(this._editor.state, pos, oldNode)) return;
		return () => {
			if (!this._editor.isEditable) return;
			const tr = restoreModified(this._editor.state, pos, oldNode);
			if (tr) this._editor.view.dispatch(tr);
		};
	}

	private _visible(pos: number) {
		return visiblePos(this._editor.state, pos);
	}

	private _origin(layer: HTMLElement): LayerOrigin {
		const dom = this._editor.view.dom;
		const { left, top } = layer.getBoundingClientRect();
		const textLeft = (dom.firstElementChild ?? dom).getBoundingClientRect().left;
		return { left, top, textLeft };
	}

	// `coordsAtPos` at a block end lands in the next block; measure the block box instead.
	private _blockDom(diffLine: ProseMirrorDiffLine): HTMLElement | null {
		if (diffLine.type === "deleted") return null;
		const pos = this._visible(diffLine.pos.from);
		if (pos !== diffLine.pos.from) return null;
		const dom = this._editor.view.nodeDOM(pos);
		return dom instanceof HTMLElement ? dom : null;
	}

	private _lineElement(diffLine: ProseMirrorDiffLine, dom: HTMLElement | null): HTMLElement | null {
		if (dom) return dom;
		const pos = diffLine.type === "deleted" ? diffLine.insertAfter : diffLine.pos.from;
		const safePos = Math.min(pos, this._editor.state.doc.content.size);
		const { node } = this._editor.view.domAtPos(this._visible(safePos));
		return node instanceof HTMLElement ? node : node.parentElement;
	}

	// An inactive tab is collapsed to zero height and its content keeps full height under `visibility: hidden`.
	private _shownAncestor(element: HTMLElement | null): HTMLElement | null {
		const root = this._editor.view.dom;
		const isShown = (node: HTMLElement) =>
			node.getBoundingClientRect().height > 0 && getComputedStyle(node).visibility !== "hidden";
		let current = element;
		while (current && current !== root && !isShown(current)) current = current.parentElement;
		return current;
	}

	// A wide table bleeds left past the text column (`WidthWrapper`), so the axis moves out with it.
	private static _contentLeft(element: HTMLElement | null, textLeft: number): number {
		const wrapper = element?.closest(".width-wrapper") ?? element?.querySelector(".width-wrapper");
		const content = wrapper?.querySelector("table");
		if (!wrapper || !content) return textLeft;
		const shownLeft = Math.max(wrapper.getBoundingClientRect().left, content.getBoundingClientRect().left);
		return Math.min(textLeft, shownLeft);
	}

	private _geometry(diffLine: ProseMirrorDiffLine, origin: LayerOrigin, axis: number): LineGeometry {
		const dom = this._blockDom(diffLine);
		const element = this._lineElement(diffLine, dom);
		const shown = this._shownAncestor(element);
		const contentLeft =
			diffLine.type === "deleted" ? origin.textLeft : DiffLines._contentLeft(element, origin.textLeft);
		const left = contentLeft - origin.left + axis - 1.5 + (diffLine.type === "comment" ? 5 : 0);
		const view = this._editor.view;

		if (shown && shown !== element) {
			const rect = shown.getBoundingClientRect();
			return { top: rect.top - origin.top, left, height: rect.height };
		}

		if (diffLine.type === "deleted") {
			// `insertAfter` is already the insertion point; do not shift it.
			const safePos = Math.min(diffLine.insertAfter, this._editor.state.doc.content.size);
			const top = view.coordsAtPos(this._visible(safePos)).bottom - origin.top;
			return { top, left, height: this._deletedDiffLineHeight };
		}

		if (dom) {
			const rect = dom.getBoundingClientRect();
			const inset = dom.closest("td, th, tr") ? this._tableLineInset : -this._diffLinesStratchPixels;
			return { top: rect.top - origin.top + inset, left, height: rect.height - inset * 2 };
		}

		const coordsStart = view.coordsAtPos(this._visible(diffLine.pos.from));
		const coordsEnd = view.coordsAtPos(this._visible(diffLine.pos.to + 1)); // +1 to include the last character
		return {
			top: coordsStart.top - origin.top - this._diffLinesStratchPixels,
			left,
			height: coordsEnd.bottom - coordsStart.top + this._diffLinesStratchPixels * 2,
		};
	}

	// Fallback for `--diff-column-axis` in `styles/diff.css`; keep in sync.
	private static readonly _defaultColumnAxis = -16;

	private _columnAxis(): number {
		const raw = getComputedStyle(this._editor.view.dom).getPropertyValue("--diff-column-axis");
		const axis = Number.parseFloat(raw);
		return Number.isNaN(axis) ? DiffLines._defaultColumnAxis : axis;
	}

	private _addNewRenderData(count: number) {
		const documentFragment = document.createDocumentFragment();
		const renderData = this._getRenderData();
		for (let i = 0; i < count; i++) {
			const diffLineElement = document.createElement("div");
			diffLineElement.dataset.type = "diff-line";
			const root = createRoot(diffLineElement);
			renderData.push({ root, element: diffLineElement });
			documentFragment.appendChild(diffLineElement);
		}
		this._setRenderData(renderData);
		this._getLayer()?.appendChild(documentFragment);
	}

	private _removeOldRenderData(count: number) {
		const renderData = this._getRenderData();
		for (let i = renderData.length - 1; i >= renderData.length - count; i--) {
			const { element, root } = renderData[i];
			root.unmount();
			element.remove();
		}
		renderData.splice(renderData.length - count, count);
		this._setRenderData(renderData);
	}

	private _getRenderData() {
		return DiffLines._editorRenderData.get(this._editor) ?? [];
	}

	private _setRenderData(renderData: { root: Root; element: HTMLElement }[]) {
		DiffLines._editorRenderData.set(this._editor, renderData);
	}

	private _onEditorDestroy() {
		this._removeOldRenderData(this._getRenderData().length);
		DiffLines._editorRenderData.delete(this._editor);
		this._layer?.remove();
	}
}

const DiffExtension = Extension.create<DiffExtensionProps, DiffExtensionStore>({
	name: "diffLines",

	addOptions() {
		return {
			articleRef: null,
			isLeftPinned: true,
			articlePath: null,
			apiUrlCreator: null,
			pageDataContext: null,
			catalogProps: null,
			oldScope: undefined,
			newScope: undefined,
		};
	},

	addStorage() {
		return {
			isLeftPinned: this.options.isLeftPinned,
			diffLines: [],
			movedMarkers: [],
			changes: [],
			articlePath: this.options.articlePath,
			oldScope: this.options.oldScope,
			newScope: this.options.newScope,
		};
	},

	addCommands() {
		return {
			updateDiffLinesModel:
				(diffLines) =>
				({ editor }) => {
					editor.storage.diffLines.diffLines = diffLines;
					return true;
				},
			updateMovedMarkers:
				(markers) =>
				({ editor, tr, dispatch }) => {
					editor.storage.diffLines.movedMarkers = markers;
					// Without the meta the plugin never rebuilds: the model arrives in a doc-preserving transaction.
					if (dispatch) tr.setMeta(movedMarkersKey, true);
					return true;
				},
			updateDiffChanges:
				(changes) =>
				({ editor, tr, dispatch }) => {
					editor.storage.diffLines.changes = changes;
					if (dispatch) tr.setMeta(linkChangesKey, true).setMeta(inlineComponentChangesKey, true);
					return true;
				},
			updateDiffContext:
				(context) =>
				({ editor }) => {
					Object.assign(editor.storage.diffLines, context);
					return true;
				},
			updateIsLeftPinned:
				(isLeftPinned, triggerUpdate = true) =>
				({ editor }) => {
					editor.storage.diffLines.isLeftPinned = isLeftPinned;
					if (triggerUpdate) editor.commands.focus(undefined, { scrollIntoView: false });
					return true;
				},
		};
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey("diff-lines"),
				view: () => new DiffLines(this.editor),
			}),
			collapseDeleted(
				() => this.editor.storage.diff?.changes,
				() => getCachedSetting("contentCompare.collapseThreshold"),
			),
			deletedRestore(() => this.editor.storage.diff?.changes),
			insertedText(() => this.editor.storage.diff?.changes),
			// Canonical set: the library's set misses link href changes.
			linkChanges(() => this.editor.storage.diffLines.changes),
			inlineComponentChanges(() => this.editor.storage.diffLines.changes),
			movedMarkersPlugin(() => this.editor.storage.diffLines.movedMarkers),
			onlyChanges(),
			caretInText(),
		];
	},
});

export default DiffExtension;
